import React from 'react';

import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Select, Row, Col, Typography, Alert, Tag, Card, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  EMPTY_SHIPPING_CODE_VALUE,
  getPlatformShippingCodes,
} from '../../../constants/ecAccountPlatforms';

const { Text } = Typography;

const EMPTY_OPTION_VALUE = EMPTY_SHIPPING_CODE_VALUE;

const EcShippingTypeMappings = ({
  value = [],
  onChange,
  shippingTypes = [],
  moduleName,
  allowCustomCode = true
}) => {
  const { t } = useTranslation();
  const predefinedCodes = getPlatformShippingCodes(moduleName);

  // 添加新映射
  const handleAdd = (code = null) => {
    const actualCode = code === EMPTY_OPTION_VALUE ? '' : code;
    const newItem = {
      key: Date.now(),
      ec_platform_shipping_code: actualCode,
      shipping_type_id: null
    };
    onChange([...value, newItem]);
  };

  // 移除映射
  const handleRemove = (item) => {
    onChange(value.filter(v => (v.key || v.id) !== (item.key || item.id)));
  };

  // 計算尚未設定的建議代碼
  const missingCodes = predefinedCodes.filter(pre =>
    !value.some(curr => {
      const currCode = curr.ec_platform_shipping_code === '' ? EMPTY_OPTION_VALUE : curr.ec_platform_shipping_code;
      return currCode === pre.value;
    })
  );

  // 更新電商平台物流代碼
  const handleCodeChange = (item, newCodeRaw) => {
    let newCode = newCodeRaw;
    if (newCode === EMPTY_OPTION_VALUE) {
      newCode = '';
    } else if (newCode === undefined || newCode === null || newCode === '') {
      newCode = null;
    }

    onChange(value.map(v =>
      (v.key || v.id) === (item.key || item.id)
        ? { ...v, ec_platform_shipping_code: newCode }
        : v
    ));
  };

  // 更新倉庫端物流
  const handleShippingTypeChange = (item, shippingTypeId) => {
    onChange(value.map(v =>
      (v.key || v.id) === (item.key || item.id)
        ? { ...v, shipping_type_id: shippingTypeId }
        : v
    ));
  };

  // 過濾已使用的電商平台物流代碼
  const getAvailableCodes = (currentCode) => {
    const currentCodeCheck = currentCode === '' ? EMPTY_OPTION_VALUE : currentCode;

    const usedCodes = value
      .map(item => item.ec_platform_shipping_code)
      .map(c => c === '' ? EMPTY_OPTION_VALUE : c)
      .filter(c => c !== null && c !== undefined && c !== currentCodeCheck);

    return predefinedCodes.filter(code => !usedCodes.includes(code.value));
  };

  const getSelectValue = (code) => {
    if (code === '') return [EMPTY_OPTION_VALUE];
    if (code !== null && code !== undefined) return [code];
    return [];
  };

  return (
    <div>
      {value.map(item => (
        <Card size="small" key={item.key || item.id} style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={10}>
               <div style={{ marginBottom: 8 }}>
                 <Text strong>{t('models.ec_account.ec_platform_shipping_code')}</Text>
               </div>
              {allowCustomCode ? (
                <Select
                  mode="tags"
                  maxTagCount={1}
                  style={{ width: '100%' }}
                  placeholder={t('models.ec_account.ec_platform_shipping_code_placeholder')}
                  value={getSelectValue(item.ec_platform_shipping_code)}
                  onChange={(values) => handleCodeChange(item, values && values.length > 0 ? values[values.length - 1] : null)}
                  options={getAvailableCodes(item.ec_platform_shipping_code)}
                  tagRender={(props) => {
                    const { label } = props;
                    return (
                      <span style={{ marginRight: 3 }}>
                        {label}
                      </span>
                    );
                  }}
                />
              ) : (
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  placeholder={t('models.ec_account.ec_platform_shipping_code_placeholder')}
                  value={item.ec_platform_shipping_code === '' ? EMPTY_OPTION_VALUE : (item.ec_platform_shipping_code || undefined)}
                  onChange={(code) => handleCodeChange(item, code)}
                  options={getAvailableCodes(item.ec_platform_shipping_code)}
                />
              )}
            </Col>

            <Col span={12}>
               <div style={{ marginBottom: 8 }}>
                 <Text strong>{t('models.ec_account.warehouse_shipping_type', { defaultValue: '倉庫端物流' })}</Text>
               </div>
              <Select
                style={{ width: '100%' }}
                allowClear
                placeholder={t('models.ec_account.warehouse_shipping_type_placeholder')}
                value={item.shipping_type_id || undefined}
                onChange={(id) => handleShippingTypeChange(item, id)}
                showSearch
                optionFilterProp="label"
                options={shippingTypes
                  .filter(type => type.source !== 'platform')
                  .map(type => ({
                    value: type.id,
                    label: t(`shipping_types.${type.code}`, { defaultValue: type.name || type.code })
                  }))}
              />
            </Col>

            <Col span={2} style={{ textAlign: 'right' }}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemove(item)}
              />
            </Col>
          </Row>
        </Card>
      ))}

      <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
        {missingCodes.length > 0 && (
          <Alert
            message={t('models.ec_account.suggested_shipping_codes', { defaultValue: '系統建議補充以下物流設定（點擊加入）' })}
            description={
              <div style={{ marginTop: 8 }}>
                {missingCodes.map(code => (
                  <Tag
                    key={code.value}
                    color="blue"
                    style={{ cursor: 'pointer', marginBottom: 4 }}
                    onClick={() => handleAdd(code.value)}
                  >
                    <PlusOutlined /> {code.label}
                  </Tag>
                ))}
              </div>
            }
            type="info"
            showIcon
          />
        )}

        {allowCustomCode && (
          <Button
            type="dashed"
            onClick={() => handleAdd(null)}
            style={{ width: '100%' }}
            icon={<PlusOutlined />}
          >
            {t('models.ec_account.add_shipping_mapping', { defaultValue: '新增物流對應' })}
          </Button>
        )}
      </Space>
    </div>
  );
};

export default EcShippingTypeMappings;
